import { useState, useEffect, useCallback } from 'react';
import { AlertTriangle, X, ExternalLink, RefreshCw, Download } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { callEdgeFunction } from '../lib/edgeFunctions';
import ModalPortal from './ModalPortal';

interface GraphHealth {
  subscription_active: boolean;
  subscription_expires_soon: boolean;
  token_valid: boolean | null;
  last_email_received: string | null;
  hours_since_last_email: number | null;
  no_emails_warning: boolean;
  issues: string[];
}

const SESSION_KEY = 'graph_health_modal_dismissed';

export default function GraphHealthAlert() {
  const { profile } = useAuth();
  const navigate = useNavigate();
  const [health, setHealth] = useState<GraphHealth | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [backfillLoading, setBackfillLoading] = useState(false);
  const [backfillResult, setBackfillResult] = useState<string | null>(null);

  const isAdmin = profile?.role === 'Admin';

  const checkHealth = useCallback(async () => {
    if (!isAdmin) return;
    setLoading(true);
    try {
      const result = await callEdgeFunction('manage-purchase-subscription', { action: 'status' });
      if (result.health) {
        const h = result.health as GraphHealth;
        setHealth(h);
        if (h.issues.length > 0 && !sessionStorage.getItem(SESSION_KEY)) {
          setShowModal(true);
        }
      }
    } catch {
      // Silently fail — non-critical UI element
    } finally {
      setLoading(false);
    }
  }, [isAdmin]);

  useEffect(() => {
    checkHealth();
  }, [checkHealth]);

  const dismissModal = () => {
    setShowModal(false);
    sessionStorage.setItem(SESSION_KEY, Date.now().toString());
  };

  const goToSettings = () => {
    dismissModal();
    navigate('/?tab=settings');
  };

  const handleBackfill = async () => {
    setBackfillLoading(true);
    setBackfillResult(null);
    try {
      const result = await callEdgeFunction('manage-purchase-subscription', { action: 'backfill' });
      const totalNew = (result.total_new as number) || 0;
      const totalSkipped = (result.total_skipped as number) || 0;
      if (totalNew > 0) {
        setBackfillResult(`${totalNew} factuur${totalNew !== 1 ? 'en' : ''} opgehaald! ${totalSkipped} al verwerkt.`);
      } else {
        setBackfillResult(`Geen nieuwe facturen gevonden (${totalSkipped} al verwerkt).`);
      }
    } catch (err) {
      setBackfillResult(`Fout: ${err instanceof Error ? err.message : 'Onbekend'}`);
    } finally {
      setBackfillLoading(false);
    }
  };

  if (!isAdmin || !health || health.issues.length === 0) {
    return null;
  }

  return (
    <>
      {/* Persistent banner — always visible when there are issues */}
      <div className="bg-red-600 text-white">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-2">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-2 min-w-0">
              <AlertTriangle className="h-4 w-4 flex-shrink-0" />
              <p className="text-sm font-medium truncate">
                Inkoopfacturen e-mail koppeling: {health.issues[0]}
                {health.issues.length > 1 && ` (+${health.issues.length - 1})`}
              </p>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <button
                onClick={() => navigate('/?tab=settings')}
                className="text-xs bg-white/20 hover:bg-white/30 px-2.5 py-1 rounded transition-colors font-medium"
              >
                Instellingen
              </button>
              <button
                onClick={checkHealth}
                disabled={loading}
                className="p-1 hover:bg-white/20 rounded transition-colors"
                aria-label="Opnieuw checken"
              >
                <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Modal — shown once per session */}
      {showModal && (
        <ModalPortal>
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
            <div
              className="fixed inset-0 bg-black/60 backdrop-blur-sm"
              onClick={dismissModal}
            />
            <div className="relative bg-white dark:bg-gray-900 rounded-xl shadow-2xl max-w-lg w-full p-6 animate-slide-up">
              <button
                onClick={dismissModal}
                className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                aria-label="Sluiten"
              >
                <X className="h-5 w-5" />
              </button>

              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center flex-shrink-0">
                  <AlertTriangle className="h-6 w-6 text-red-600" />
                </div>
                <div className="flex-1">
                  <h2 className="text-lg font-bold text-gray-900 dark:text-gray-100">
                    E-mail koppeling probleem
                  </h2>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                    Er zijn problemen met de Microsoft Graph API koppeling voor inkoopfacturen.
                    Hierdoor worden nieuwe inkoopfacturen niet automatisch opgehaald.
                  </p>
                </div>
              </div>

              <div className="mt-5 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
                <h3 className="text-sm font-semibold text-red-900 dark:text-red-200 mb-2">
                  Gedetecteerde problemen:
                </h3>
                <ul className="space-y-1.5">
                  {health.issues.map((issue, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-red-700 dark:text-red-300">
                      <span className="text-red-400 mt-0.5">•</span>
                      <span>{issue}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {health.last_email_received && (
                <p className="mt-3 text-xs text-gray-500 dark:text-gray-400">
                  Laatste inkoopfactuur ontvangen:{' '}
                  <span className="font-medium text-gray-700 dark:text-gray-300">
                    {new Date(health.last_email_received).toLocaleDateString('nl-NL', {
                      day: 'numeric',
                      month: 'long',
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                  </span>
                  {health.hours_since_last_email && (
                    <span className="text-red-600 dark:text-red-400 font-medium">
                      {' '}({health.hours_since_last_email} uur geleden)
                    </span>
                  )}
                </p>
              )}

              {health.last_email_received && (
                <div className="mt-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3">
                  <div className="flex items-center justify-between">
                    <p className="text-sm text-blue-800 dark:text-blue-300">
                      Gemiste facturen ophalen sinds laatste ontvangst?
                    </p>
                    <button
                      onClick={handleBackfill}
                      disabled={backfillLoading}
                      className="px-3 py-1.5 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 flex items-center gap-1.5 text-xs font-medium whitespace-nowrap"
                    >
                      {backfillLoading ? (
                        <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white" />
                      ) : (
                        <Download className="h-3 w-3" />
                      )}
                      {backfillLoading ? 'Bezig...' : 'Ophalen'}
                    </button>
                  </div>
                  {backfillResult && (
                    <p className="text-xs text-blue-700 dark:text-blue-400 mt-2">{backfillResult}</p>
                  )}
                </div>
              )}

              <div className="mt-6 flex items-center gap-3">
                <button
                  onClick={goToSettings}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium text-sm"
                >
                  <ExternalLink className="h-4 w-4" />
                  Ga naar Instellingen
                </button>
                <button
                  onClick={dismissModal}
                  className="px-4 py-2.5 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors font-medium text-sm"
                >
                  Later
                </button>
              </div>
            </div>
          </div>
        </ModalPortal>
      )}
    </>
  );
}
